

import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fechar menu mobile ao mudar de rota
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <header 
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled 
          ? 'bg-black/90 backdrop-blur-md shadow-lg' 
          : 'bg-black'
      }`}
    >
      <nav className="container-responsive">
        <div className="flex items-center justify-between py-2 xs:py-2.5 xs2:py-3 sm:py-3 md:py-4">
          {/* Logo - Responsivo para todos os breakpoints */}
          <Link 
            to="/" 
            className="flex items-center gap-1 xs:gap-2 sm:gap-3 
                      focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg"
            aria-label="Ir para página inicial"
          >
            <img
              src="/images/logo.png"
              alt="Logo notemaster"
              className="h-6 xs:h-7 xs2:h-8 xs3:h-9 xs4:h-10 sm:h-10 md:h-12 
                         lg:h-14 w-auto object-contain transition-transform 
                         hover:scale-105"
              loading="eager"
            />
          </Link>

          {/* Botão menu mobile - Visível até sm */}
          <button
            className="sm:hidden text-white p-2 rounded-lg hover:bg-gray-800 
                      transition-colors focus:outline-none focus:ring-2 
                      focus:ring-primary-500"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Fechar menu" : "Abrir menu"}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? (
              <X size={20} className="xs:w-6 xs:h-6 xs2:w-7 xs2:h-7" />
            ) : (
              <Menu size={20} className="xs:w-6 xs:h-6 xs2:w-7 xs2:h-7" />
            )}
          </button>

          {/* Navegação desktop - Visível a partir de sm */}
          <ul className="hidden sm:flex gap-4 md:gap-6 lg:gap-8 xl:gap-10 
                       text-white font-medium text-xs sm:text-sm md:text-base">
            {[
              { path: '/', label: 'Início' },
              { path: '/contato', label: 'Contato' },
              { path: '/sobre', label: 'Sobre' },
            ].map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <li key={link.path} className="relative">
                  <Link 
                    to={link.path}
                    className={`relative px-2 py-1 rounded transition-colors
                              focus:outline-none focus:ring-2 focus:ring-primary-500
                              ${
                                isActive 
                                  ? 'text-primary-400' 
                                  : 'hover:text-primary-400'
                              }`}
                  >
                    {link.label}
                    {isActive && (
                      <motion.span
                        layoutId="activeIndicator"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500 rounded-full"
                        initial={false}
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </nav>

      {/* Navegação mobile - Slide in animado com Framer Motion */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="sm:hidden bg-black/95 backdrop-blur-md border-t border-gray-800 
                       overflow-hidden"
          >
            <motion.ul 
              initial={{ y: -20 }}
              animate={{ y: 0 }}
              exit={{ y: -20 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col py-3 xs:py-4 px-4 xs:px-6 gap-2"
            >
              {[
                { path: '/', label: 'Início' },
                { path: '/contato', label: 'Contato' },
                { path: '/sobre', label: 'Sobre' },
              ].map((link, index) => {
                const isActive = location.pathname === link.path;
                return (
                  <motion.li
                    key={link.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={link.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`block px-4 py-2.5 xs:py-3 rounded-lg text-sm xs:text-base 
                                font-medium transition-all duration-200
                                ${
                                  isActive
                                    ? 'bg-primary-600 text-white'
                                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                                }`}
                    >
                      {link.label}
                    </Link>
                  </motion.li>
                );
              })}
            </motion.ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;












